import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Field } from 'msp-components'
import { JasminDhlPackstation } from '@/components/organisms/JasminDhlPackstation'
import getPackstationAddressFields from './PackstationAddressFields.fields'
import { PackstationAddressFieldsProps } from './PackstationAddressFields.types'

interface Packstation {
  packstationKeyword?: string
  packstationNumber?: string
  name?: string
  streetAddress?: string
  country?: string
  postalCode?: string
  city?: string
  type?: string
}

const PackstationAddressFields = ({
  className,
  form,
  isLocker = false
}: PackstationAddressFieldsProps) => {
  const { formatMessage } = useIntl()
  const fieldConfig = getPackstationAddressFields()
  const [postNumberFieldConfig, setPostNumberFieldConfig] = useState(
    fieldConfig.address2
  )

  useEffect(() => {
    handlePostNumberConfig(isLocker)
  }, [])

  if (!form) return null

  function handlePostNumberConfig(isLocker: boolean) {
    const postNumberConfig = fieldConfig.address2

    if (isLocker) {
      postNumberConfig.rules.required = formatMessage({
        defaultMessage:
          'You need to enter the Post Number if you have selected a Packstation (locker).',
        id: 'msp.address_form.post_number_required'
      })

      postNumberConfig.label = formatMessage({
        defaultMessage: 'DHL Post Number',
        id: 'msp.address_form.label.dhl_post_number_required'
      })
    }

    setPostNumberFieldConfig(postNumberConfig)
  }

  const onPackstationSelect = (packstation: Packstation) => {
    const address =
      packstation.packstationKeyword && packstation.packstationNumber
        ? `${packstation.packstationKeyword} ${packstation.packstationNumber}`
        : `${packstation.name || 'Postfiliale'} / ${packstation.streetAddress}`

    form.setValue('address1', address)
    form.setValue('countryCode', packstation.country)
    form.setValue('postalCode', packstation.postalCode)
    form.setValue('city', packstation.city)
    form.setValue('c_isPackstation', true)
    form.setValue('c_isLocker', packstation?.type === 'locker')

    handlePostNumberConfig(packstation?.type === 'locker')

    form.clearErrors()
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className={'mb-4'}>
        {formatMessage({
          defaultMessage:
            'Select a packstation near you by clicking on the map. Then enter your post number in the form. The post number is your personal DHL customer number, which is assigned to you after registering for a DHL customer account.',
          id: 'msp.address_form.packstation_text'
        })}
      </div>
      <div className='-mx-6 md:mx-0'>
        <JasminDhlPackstation onPackstationSelect={onPackstationSelect} />
      </div>
      <div className='flex flex-row'>
        <Field
          control={form.control}
          config={fieldConfig.salutation}
          error={form.formState.errors?.salutation}
          isDisabled={form.formState.isSubmitting}
        />
      </div>
      <div className='flex flex-col md:flex-row md:space-x-3'>
        <Field
          control={form.control}
          config={fieldConfig.firstName}
          error={form.formState.errors?.firstName}
          isDisabled={form.formState.isSubmitting}
        />
        <Field
          control={form.control}
          config={fieldConfig.lastName}
          error={form.formState.errors?.lastName}
          isDisabled={form.formState.isSubmitting}
        />
      </div>
      <Field
        control={form.control}
        config={postNumberFieldConfig}
        error={form.formState.errors?.address2}
        isDisabled={form.formState.isSubmitting}
      />
      <Field
        control={form.control}
        config={fieldConfig.address1}
        error={form.formState.errors?.address1}
        isDisabled
      />
      <div className='grid grid-cols-1 md:grid-cols-3 md:gap-3'>
        <Field
          control={form.control}
          config={fieldConfig.postalCode}
          error={form.formState.errors?.postalCode}
          isDisabled
        />
        <div className='col-span-2'>
          <Field
            control={form.control}
            config={fieldConfig.city}
            error={form.formState.errors?.city}
            isDisabled
          />
        </div>
      </div>
      <Field
        className='mt-3'
        control={form.control}
        config={fieldConfig.countryCode}
        error={form.formState.errors?.countryCode}
        isDisabled={true}
      />
      <input
        name='c_isPackstation'
        id='c_isPackstation'
        type='hidden'
        aria-hidden='true'
      />
      <input
        name='c_isLocker'
        id='c_isLocker'
        type='hidden'
        aria-hidden='true'
      />
    </div>
  )
}

export default PackstationAddressFields
